<template>
  <v-container>
    <material-card
      :heading="`${contract.contract_number} | ${contract.contract_name}`"
      color="accent"
    >
      <v-card-text
        class="pa-4"
      >
        <v-card
          tile
          elevation="0"
        >
          <v-row class="text-center">
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Account Manager"
                :body="contract.account_manager"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Status"
                :body="contract.status"
              />
            </v-col>
            <v-col v-if="contract.assyst_software_id !== null && getAssystSoftware(contract.assyst_software_id) !== undefined" sm="6" md="4" lg="3">
              <details-card
                title="Assyst Version"
                :body="getAssystSoftware(contract.assyst_software_id).assyst_version"
                copyTooltip
                :copyValue="getAssystSoftware(contract.assyst_software_id).assyst_package_name"
                copyTooltipText="Copy Package Name"
                softwareChip
                :softwareDate="getAssystSoftware(contract.assyst_software_id).expiry_date"
                :softwareLink="`/software/assyst/${getAssystSoftware(contract.assyst_software_id).assyst_software_id}`"
              />
            </v-col>
            <v-col v-if="contract.assyst_software_id !== null && getUrl(contract.contract_id, 'assyst') !== undefined" sm="6" md="4" lg="3">
              <details-card
                title="URL"
                :body="getUrl(contract.contract_id, 'assyst').url"
                copyTooltip
                :copyValue="getUrl(contract.contract_id, 'assyst').url"
                copyTooltipText="Copy URL"
                urlTooltip
                :urlValue="getUrl(contract.contract_id, 'assyst').url"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Authentication Type"
                :body="contract.authentication"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Location"
                :body="getRegionText(contract.region_id)"
              />
            </v-col>
            <v-col v-if="contract.assyst_software_id !== null" sm="6" md="4" lg="3">
              <details-card
                title="Database Type"
                :body="getDbText()"
              />
            </v-col>
            <v-col v-if="contract.ipaas_software_id !== null && getIpaasSoftware(contract.ipaas_software_id) !== undefined" sm="6" md="4" lg="3">
              <details-card
                title="iPaaS Version"
                :body="getIpaasSoftware(contract.ipaas_software_id).ipaas_version"
                copyTooltip
                copyTooltipText="Copy Package Name"
                :copyValue="getIpaasSoftware(contract.ipaas_software_id).ipaas_package_name"
                softwareChip
                :softwareDate="getIpaasSoftware(contract.ipaas_software_id).expiry_date"
                :softwareLink="`/software/ipaas/${getIpaasSoftware(contract.ipaas_software_id).ipaas_software_id}`"
              />
            </v-col>
            <v-col v-if="contract.database_type !== 'Azure SQL' && contract.sql_load_balancer_id !== null" sm="6" md="4" lg="3">
              <details-card
                title="Process IaaS Backups"
                :body="contract.process_backups === 'y' ? 'Yes' : 'No'"
              />
            </v-col>
            <v-col v-if="contract.database_type !== 'Azure SQL' && contract.sql_load_balancer_id !== null" sm="6" md="4" lg="3">
              <details-card
                title="Process CMDB Backups"
                :body="contract.process_cmdb_backups === 'y' ? 'Yes' : 'No'"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="WAF Type"
                :body="contract.waf_type"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3" v-if="contract.itom_software_id !== null && getUrl(contract.contract_id, 'itom') !== undefined">
              <details-card
                title="ITOM URL"
                :body="getUrl(contract.contract_id, 'itom').url"
                copyTooltip
                :copyValue="getUrl(contract.contract_id, 'itom').url"
                copyTooltipText="Copy URL"
                urlTooltip
                :urlValue="getUrl(contract.contract_id, 'itom').url"
              />
            </v-col>
            <v-col v-if="contract.itom_software_id !== null && getItomSoftware(contract.itom_software_id) !== undefined" sm="6" md="4" lg="3">
              <details-card
                title="ITOM Version"
                :body="getItomSoftware(contract.itom_software_id).itom_version"
                copyTooltip
                copyTooltipText="Copy Package Name"
                :copyValue="getItomSoftware(contract.itom_software_id).itom_package_name"
                softwareChip
                :softwareDate="getItomSoftware(contract.itom_software_id).expiry_date"
                :softwareLink="`/software/itom/${getItomSoftware(contract.itom_software_id).itom_software_id}`"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3" v-if="!isProduction">
              <details-card
                title="DB Refreshes"
                :body="dbRefreshText"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3" v-if="userHasPermission('edit_contract')">
              <details-card
                title="Max DB Size"
                :body="dbMaxSizeText"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3" v-if="userHasPermission('edit_contract')">
              <details-card
                title="Max Web Servers"
                :body="maxWebServersText"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Prevent Restarts"
                :body="contract.restart_locked === 'y' ? 'Yes' : 'No'"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Prevent Redeploys"
                :body="contract.redeploy_locked === 'y' ? 'Yes' : 'No'"
              />
            </v-col>
            <v-col sm="6" md="4" lg="3">
              <details-card
                title="Using Assyst AI"
                :body="contract.using_assystai === 'y' ? 'Yes' : 'No'"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-card-text>
      <v-card-actions
        v-if="(userHasPermission('edit_contract') || userHasPermission('edit_sizing') || userHasPermission('decom_contract')) && adminToggle"
      >
        <v-spacer />
        <decom-dialog
          v-if="userHasPermission('decom_contract') && contract.status !== 'Decommissioned'"
          :contractNumber="contract.contract_number"
          @decomContract="decomContract"
        />
        <v-dialog
          v-if="(userHasPermission('edit_contract') || userHasPermission('edit_sizing')) && contract.status !== 'Decommissioned'"
          v-model="editDialog"
          persistent
          width="1000"
        >
          <template v-slot:activator="{ on: dialog, attrs }">
            <v-tooltip
              top
              color="primary"
            >
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...dialog }"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit Contract</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title class="text-h5 accent lighten-2">
              Edit Contract
            </v-card-title>

            <v-card-text>
              <v-container>
                <v-form
                  ref="contractForm"
                >
                  <v-row>
                    <v-col sm="6" md="4" lg="4" v-if="contract.assyst_software_id !== null && getUrl(contract.contract_id, 'assyst') !== undefined">
                      <v-text-field
                        v-model="currentUrl"
                        :disabled="!userHasPermission('edit_contract')"
                        :rules="domainRules"
                        label="URL"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.itom_software_id !== null && getUrl(contract.contract_id, 'itom') !== undefined">
                      <v-text-field
                        v-model="currentItomUrl"
                        :disabled="!userHasPermission('edit_contract')"
                        :rules="domainRules"
                        label="ITOM URL"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-combobox
                        v-model="currentStatus"
                        :items="status_types"
                        :disabled="!userHasPermission('edit_contract')"
                        label="Status"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-combobox
                        v-model="selectedAuth"
                        :items="auth_types"
                        :disabled="!userHasPermission('edit_contract')"
                        label="Authentication"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.assyst_software_id !== null">
                      <v-select
                        v-model="softwareVersion"
                        :items="assyst_software"
                        :disabled="!userHasPermission('edit_contract')"
                        item-text="assyst_version"
                        item-value="assyst_software_id"
                        label="Assyst Version"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.ipaas_software_id !== null && getIpaasSoftware(contract.ipaas_software_id) !== undefined">
                      <v-select
                        v-model="ipaasVersion"
                        :items="ipaas_software"
                        :disabled="!userHasPermission('edit_contract')"
                        item-text="ipaas_version"
                        item-value="ipaas_software_id"
                        label="IPaaS Version"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.itom_software_id !== null && getItomSoftware(contract.itom_software_id) !== undefined">
                      <v-select
                        v-model="itomVersion"
                        :items="itom_software"
                        :disabled="!userHasPermission('edit_contract')"
                        item-text="itom_version"
                        item-value="itom_software_id"
                        label="ITOM Version"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.assyst_software_id !== null">
                      <v-autocomplete
                        v-model="sqlLoadBalancer"
                        :items="sql_load_balancers"
                        :disabled="!userHasPermission('edit_contract')"
                        :item-text="item => getLbName(item.sql_load_balancer_ip, item.availability_group)"
                        item-value="sql_load_balancer_id"
                        label="SQL Load Balancer"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-switch
                        v-model="usingAzureSql"
                        label="Using AzureSQL"
                        :disabled="!userHasPermission('edit_contract')"
                        true-value="y"
                        false-value="n"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="userHasPermission('edit_sizing')">
                      <v-text-field
                        v-model="maxDbSize"
                        :rules="dbSizeRules"
                        label="Max DB Size (GB)"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="userHasPermission('edit_sizing')">
                      <v-text-field
                        v-model="maxWebServers"
                        :rules="dbSizeRules"
                        label="Max Web Servers"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-combobox
                        v-model="currentWafType"
                        :items="waf_types"
                        :disabled="!userHasPermission('edit_contract')"
                        label="WAF Type"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.database_type !== 'Azure SQL' && contract.sql_load_balancer_id !== null">
                      <v-switch
                        v-model="currentProcessBackups"
                        label="Process IaaS Backups"
                        :disabled="!userHasPermission('edit_contract')"
                        true-value="y"
                        false-value="n"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="contract.database_type !== 'Azure SQL' && contract.sql_load_balancer_id !== null">
                      <v-switch
                        v-model="currentProcessCmdb"
                        label="Process CMDB Backups"
                        :disabled="!userHasPermission('edit_contract')"
                        true-value="y"
                        false-value="n"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="!isProduction">
                      <v-text-field
                        v-model="maxRefreshes"
                        :disabled="!userHasPermission('edit_contract')"
                        label="Max DB Refreshes"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4" v-if="!isProduction">
                      <v-text-field
                        v-model="usedRefreshes"
                        :disabled="!userHasPermission('edit_contract')"
                        label="Used DB Refreshes"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-switch
                        v-model="currentRestartLocked"
                        label="Prevent Restarts"
                        :disabled="!userHasPermission('edit_contract')"
                        true-value="y"
                        false-value="n"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-switch
                        v-model="currentRedeployLocked"
                        label="Prevent Redeploy"
                        :disabled="!userHasPermission('edit_contract')"
                        true-value="y"
                        false-value="n"
                      />
                    </v-col>
                    <v-col sm="6" md="4" lg="4">
                      <v-switch
                        v-model="usingAssystAI"
                        label="Using AssystAI"
                        :disabled="!userHasPermission('edit_contract')"
                        true-value="y"
                        false-value="n"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                @click="editDialog = false"
              >
                CANCEL
              </v-btn>
              <v-btn
                color="accent"
                @click="updateContract()"
              >
                SAVE
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-actions>
    </material-card>
  </v-container>
</template>

<script>
  import axios from 'axios'
  import router from '@/router'
  import { authHeader } from '@/util/helpers'
  import { getAzureRegion } from '@/util/azure_regions'
  import getEnv from '@/util/env'
  import { get, sync } from 'vuex-pathify'
  const host = getEnv('VUE_APP_API_HOST')
  export default {
    name: 'ContractHeader',
    props: {
      contract: Object,
    },
    data () {
      return {
        editDialog: false,
        softwareVersion: this.contract.assyst_software_id,
        ipaasVersion: this.contract.ipaas_software_id,
        itomVersion: this.contract.itom_software_id,
        selectedAuth: this.contract.authentication,
        currentStatus: this.contract.status,
        currentUrl: null,
        currentItomUrl: null,
        currentWafType: this.contract.waf_type,
        currentProcessBackups: this.contract.process_backups,
        currentProcessCmdb: this.contract.process_cmdb_backups,
        maxRefreshes: this.contract.max_refreshes,
        usedRefreshes: this.contract.used_refreshes,
        currentRestartLocked: this.contract.restart_locked,
        currentRedeployLocked: this.contract.redeploy_locked,
        maxDbSize: this.contract.max_db_size,
        maxWebServers: this.contract.max_web_servers,
        auth_types: this.$store.getters['contract/auth_types'],
        status_types: this.$store.getters['contract/status_types'],
        waf_types: this.$store.getters['contract/waf_types'],
        sqlLoadBalancer: this.contract.sql_load_balancer_id,
        usingAzureSql: this.contract.using_azuresql,
        usingAssystAI: this.contract.using_assystai,
        domainRules: [value => {
          const pattern = /^((?!-)[A-Z0-9a-z-]{1,63}(?<!-)\.)+[A-Za-z]{2,6}$/
          if (value) {
            return pattern.test(value) || 'Invalid URL.'
          } else {
            return true
          }
        }],
        dbSizeRules: [value => {
          const numPattern = /^[0-9]*$/
          if (value) {
            return numPattern.test(value) || 'Must be a number'
          } else {
            return true
          }
        }]
      }
    },
    computed: {
      ...sync('app', [
        'adminToggle',
      ]),
      ...get('user', [
        'user',
      ]),
      ...get('settings', [
        'regions',
        'sql_servers',
        'sql_load_balancers',
        'customer_urls'
      ]),
      ...get('software', [
        'assyst',
        'ipaas',
        'itom'
      ]),
      assyst_software () {
        let unsorted = []
        if (!this.userHasPermission('access_prerelease')) {
          unsorted = this.$store.getters['software/assyst'].filter(element => element.released == 'y')
        } else {
          unsorted = this.$store.getters['software/assyst']
        }
        return unsorted.sort((a, b) => {
          return b.assyst_software_id - a.assyst_software_id
        })
      },
      ipaas_software () {
        let unsorted = []
        if (!this.userHasPermission('access_prerelease')) {
          unsorted = this.$store.getters['software/ipaas'].filter(element => element.released == 'y')
        } else {
          unsorted = this.$store.getters['software/ipaas']
        }
        return unsorted.sort((a, b) => {
          return b.ipaas_software_id - a.ipaas_software_id
        })
      },
      itom_software () {
        let unsorted = []

        unsorted = this.$store.getters['software/itom']

        return unsorted.sort((a, b) => {
          return b.itom_software_id - a.itom_software_id
        })
      },
      sql_load_balancers () {
        return this.$store.getters['settings/sql_load_balancers']
      },
      contractData () {
        const fields = {
          assyst_software_id: this.softwareVersion > 0 ? this.softwareVersion : null,
          ipaas_software_id: this.ipaasVersion > 0 ? this.ipaasVersion : null,
          itom_software_id: this.itomVersion > 0 ? this.itomVersion : null,
          authentication: this.selectedAuth,
          status: this.currentStatus,
          waf_type: this.currentWafType,
          process_backups: this.currentProcessBackups,
          process_cmdb_backups: this.currentProcessCmdb,
          restart_locked: this.currentRestartLocked,
          redeploy_locked: this.currentRedeployLocked,
          using_azuresql: this.usingAzureSql,
          using_assystai: this.usingAssystAI,
          sql_load_balancer_id: this.sqlLoadBalancer > 0 ? this.sqlLoadBalancer : null
        }
        if (this.userHasPermission('edit_sizing')) {
          fields.max_db_size = this.maxDbSize === '' ? null : this.maxDbSize
          fields.max_web_servers = this.maxWebServers === '' ? null : this.maxWebServers
        }
        // Only set/update for production contracts
        if (!this.isProduction) {
          fields.max_refreshes = this.maxRefreshes === '' ? null : this.maxRefreshes
          fields.used_refreshes = this.usedRefreshes === '' ? null : this.usedRefreshes
        }
        // Reset certain fields if status is set to decommissioned
        if (fields.status === 'Decommissioned') {
          fields.process_backups = 'n'
          fields.process_cmdb_backups = 'n'
          fields.deleted = 'y'
        }
        // AzureSQL check - Will set the 'using_azuresql' value depending on what LB is selected
        if (fields.sql_load_balancer_id > 0) {
          var lb = this.$store.getters['settings/sql_load_balancers'].find(element => element.sql_load_balancer_id == fields.sql_load_balancer_id)
          if (lb.sql_load_balancer_ip.includes('database.windows.net') || lb.sql_load_balancer_ip.includes('database.usgovcloudapi.net')) {
            fields.using_azuresql = 'y'
          } else {
            fields.using_azuresql = 'n'
          }
        }
        return fields
      },
      urlData () {
        const fields = {
          application: 'assyst',
          url: this.currentUrl,
        }
        return fields
      },
      itomUrlData () {
        const fields = {
          application: 'itom',
          url: this.currentItomUrl,
        }
        return fields
      },
      isProduction () {
        return this.contract.environment === 'Customer - Prod'
      },
      dbRefreshText () {
        if (this.contract.max_refreshes === null || this.contract.max_refreshes === '') {
          return 'Unlimited'
        } else {
          return (this.contract.used_refreshes !== null ? this.contract.used_refreshes : '0') + '/' + this.contract.max_refreshes + ' used'
        }
      },
      dbMaxSizeText () {
        if (this.contract.max_db_size === null || this.contract.max_db_size === '') {
          return 'Fair Use'
        } else {
          return this.contract.max_db_size + ' GB'
        }
      },
      maxWebServersText () {
        if (this.contract.max_web_servers === null || this.contract.max_web_servers === '') {
          return 'Not Set'
        } else {
          return this.contract.max_web_servers
        }
      },
    },
    watch: {
      contract: {
        handler () {
          this.setInitialData()
          this.populateUrls()
        },
      },
    },
    created () {
      this.setInitialData()
      this.populateUrls()
    },
    methods: {
      setInitialData: function() {
        if (this.contract.contract_id !== undefined) {
          this.softwareVersion = this.contract.assyst_software_id
          this.ipaasVersion = this.contract.ipaas_software_id
          this.itomVersion = this.contract.itom_software_id
          this.selectedAuth = this.contract.authentication
          this.currentStatus = this.contract.status
          this.currentUrl = null
          this.currentItomUrl = null
          this.currentWafType = this.contract.waf_type
          this.currentProcessBackups = this.contract.process_backups
          this.currentProcessCmdb = this.contract.process_cmdb_backups
          this.maxRefreshes = this.contract.max_refreshes
          this.usedRefreshes = this.contract.used_refreshes
          this.currentRestartLocked = this.contract.restart_locked
          this.currentRedeployLocked = this.contract.redeploy_locked
          this.maxDbSize = this.contract.max_db_size
          this.maxWebServers = this.contract.max_web_servers
          this.sqlLoadBalancer = this.contract.sql_load_balancer_id
          this.usingAzureSql = this.contract.using_azuresql
          this.usingAssystAI = this.contract.using_assystai
        }
      },
      userHasPermission: function (perm) {
        let perms = this.user.permissions.map(a => a.name)
        return perms.includes(perm)
      },
      getAssystSoftware: function (software_id) {
        let version = this.assyst.find(element => element.assyst_software_id === software_id)
        return version
      },
      getIpaasSoftware: function (software_id) {
        let version = this.ipaas.find(element => element.ipaas_software_id === software_id)
        return version
      },
      getItomSoftware: function (software_id) {
        let version = this.itom.find(element => element.itom_software_id === software_id)
        return version
      },
      getSqlLoadBalancer: function (sql_lb_id) {
        let lb = this.sql_load_balancers.find(element => element.sql_load_balancer_id === sql_lb_id)
        return lb
      },
      getSqlServer: function (sql_server_id) {
        let server = this.sql_servers.find(element => element.sql_server_id === sql_server_id)
        return server
      },
      getUrl: function (contract_id, type) {
        let url = this.customer_urls.find(element => element.contract_id === contract_id && element.application === type)
        return url
      },
      getRegionText: function (region_id) {
        let region = this.regions.find(element => element.region_id === region_id)
        let text = "Unknown"
        if (region !== undefined) {
          text = getAzureRegion(region.region_short)
        }
        return text
      },
      populateUrls: function () {
        if (this.contract.assyst_software_id !== null) {
          let assyst = this.getUrl(this.contract.contract_id, 'assyst')
          this.currentUrl = assyst !== undefined ? assyst.url : null
        }
        if (this.contract.itom_software_id !== null) {
          let itom = this.getUrl(this.contract.contract_id, 'itom')
          this.currentItomUrl = itom !== undefined ? itom.url : null
        }
      },
      getDbType: function (database) {
        let dbType = ''
        switch (database) {
          case 'y':
            dbType = 'Azure SQL'
            break
          case 'n':
            dbType = 'IaaS'
            break
          default:
            dbType = 'Unknown'
        }
        return dbType
      },
      getDbText: function() {
        let text = ''
        switch (this.contract.database_type) {
          case 'Azure SQL':
            text = 'Azure SQL (' + this.getSqlLoadBalancer(this.contract.sql_load_balancer_id).sql_load_balancer_ip + ')'
            break
          case 'IaaS':
            if (this.contract.sql_load_balancer_id !== null) {
              text = 'IaaS (' + (this.getSqlLoadBalancer(this.contract.sql_load_balancer_id).primary_server_id !== null ? this.getSqlServer(this.getSqlLoadBalancer(this.contract.sql_load_balancer_id).primary_server_id).sql_server_name : 'Unknown') + ' | ' + (this.getSqlLoadBalancer(this.contract.sql_load_balancer_id).secondary_server_id !== null ? this.getSqlServer(this.getSqlLoadBalancer(this.contract.sql_load_balancer_id).secondary_server_id).sql_server_name : 'Unknown') + ')'
            } else {
              text = 'IaaS (N/A)'
            }
            break
          default:
            text = 'Unknown'
        }
        return text
      },
      getLbName: function (ip, ag) {
        if (ag === null) {
          return ip
        } else {
          return ag
        }
      },
      decomContract: function (data) {
        axios
          .put(
            `${host}/v2/contracts/decom/` + this.$route.params.id,
            data,
            {
              headers: authHeader(),
            }
          )
          .then(() => {
            this.$snackbar.showMessage({ content: 'Contract has been decommissioned.', color: 'success', timeout: 5000 })
            setTimeout(() => router.push('/contracts/'), 2000)
          })
          .catch((err) => {
            var message = ''
            if (err.response.data === undefined) {
              message = 'An unknown error occurred'
            } else {
              message = err.response.data.message
            }
            this.$errordialog.showMessage({ content: message, color: 'error', title: 'Oops! Something went wrong.' })
          })
      },
      async updateContract () {
        if (this.$refs.contractForm.validate()) {

          try {

            // eslint-disable-next-line
            let assystPromise = new Promise((resolve, reject) => {
              resolve(1)
            })
            if (this.currentUrl !== null) {
              const assyst_url_id = this.getUrl(this.contract.contract_id, 'assyst').customer_url_id
              assystPromise = axios.put(
                `${host}/v2/customer_urls/` + assyst_url_id,
                this.urlData,
                {
                  headers: authHeader(),
                },
              )
            }
      
            // eslint-disable-next-line
            let itomPromise = new Promise((resolve, reject) => {
              resolve(1)
            })
            if (this.currentItomUrl !== null) {
              const itom_url_id = this.getUrl(this.contract.contract_id, 'itom').customer_url_id
              itomPromise = axios.put(
                `${host}/v2/customer_urls/` + itom_url_id,
                this.itomUrlData,
                {
                  headers: authHeader(),
                },
              )
            }

            await Promise.all([
              axios.put(
                `${host}/v2/contracts/` + this.$route.params.id,
                this.contractData,
                {
                  headers: authHeader(),
                },
              ),
              assystPromise,
              itomPromise,
            ]) // eslint-disable-next-line
              .then((response) => {
                this.$snackbar.showMessage({ content: 'Contract has been updated.', color: 'success', timeout: 5000 })
                this.editDialog = false
                setTimeout(() => this.$emit('updateContract'), 1000)
              })
          } catch {
            this.$errordialog.showMessage({ content: 'There was an error, please try again.', color: 'error', title: 'Oops! Something went wrong.' })
            this.editDialog = false
          }
        }
      },
    },
  }
</script>
